import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Box, Container, Paper, Stepper, Step, StepLabel, 
  Typography, TextField, Button, Grid, Divider,
  FormControl, InputLabel, Select, MenuItem,
  Autocomplete, Stack, Alert, Card, CardContent,
  LinearProgress, alpha, CircularProgress
} from '@mui/material';
import { 
  Business as BusinessIcon, 
  AccountBalance as BankIcon,
  CheckCircle as SuccessIcon,
  EmojiEvents as PlanIcon,
  Person as PersonIcon,
  VerifiedUser as SecurityIcon,
  CloudUpload as UploadIcon,
  CheckCircle as ValidatedIcon,
  BadgeOutlined as IdentityIcon,
  ShieldOutlined as ShieldIcon,
  FolderOpen as DocsIcon,
  AccountBalance as SettlementIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '@clerk/clerk-react';
import { 
  registerPartner, 
  updateBusinessDetails, 
  updateBankDetails, 
  submitKYC,
  selectPlan,
  checkPartnerStatus 
} from '../../store/slices/partnerSlice';
import ModernLoader from '../../components/common/ModernLoader';
import './PartnerOnboarding.css';

const steps = [
  { label: 'Identity', icon: <PersonIcon /> },
  { label: 'Business', icon: <BusinessIcon /> },
  { label: 'Compliance', icon: <SecurityIcon /> },
  { label: 'Settlement', icon: <BankIcon /> },
  { label: 'Network Plan', icon: <PlanIcon /> }
];

const IndianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", 
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", 
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", 
  "Uttarakhand", "West Bengal"
];

const MajorCities = [
  "Mumbai", "Delhi", "Bangalore", "Hyderabad", "Ahmedabad", "Chennai", "Kolkata", 
  "Surat", "Pune", "Jaipur", "Lucknow", "Kanpur", "Nagpur", "Indore", "Thane", "Bhopal"
];

const plans = [
  {
    id: 'Starter',
    name: 'Starter Tier',
    price: '₹0',
    interval: '/mo',
    features: ['1 Operational Center', '3 Daily Broadcasts', '8% Network Commission', 'Standard Settlement'],
    theme: '#94a3b8',
    desc: 'Local theater nodes with baseline synchronization.'
  },
  {
    id: 'Growth',
    name: 'Growth Axis',
    price: '₹4,999',
    interval: '/mo',
    popular: true,
    features: ['4 Operational Centers', 'Unlimited Broadcasts', '4% Network Commission', 'T+2 Accelerated Cycle'],
    theme: '#6366f1',
    desc: 'Scaling enterprise nodes with high-throughput features.'
  },
  {
    id: 'Pro',
    name: 'Pro Matrix',
    price: '₹14,999',
    interval: '/mo',
    features: ['Unlimited Centers', 'Zero Threshold Limits', '2% Network Commission', 'Priority Neural Sync'],
    theme: '#8b5cf6',
    desc: 'Market leaders requiring massive infrastructure scale.'
  }
];

const PartnerOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, loading: authLoading, fetchUser } = useAuth();
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { partner, onboardingStep } = useSelector(s => s.partner);
  
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [handshakeMode, setHandshakeMode] = useState(false);
  const [handshakeProgress, setHandshakeProgress] = useState(0);
  const [error, setError] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState({});

  useEffect(() => {
    if (handshakeMode) {
      const interval = setInterval(() => {
        setHandshakeProgress(v => {
          if (v >= 100) {
            clearInterval(interval);
            return 100;
          }
          return v + 2;
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [handshakeMode]);
  useEffect(() => {
    if (partner?.documents) {
      const docsMap = {};
      partner.documents.forEach(d => { docsMap[d.type] = d; });
      setUploadedDocs(docsMap);
    }
  }, [partner]);

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const targetId = user?.clerkId || user?.id || clerkUser?.id;
    setSaving(true);
    setError('');

    try {
      const { uploadKYCDocument } = await import('../../store/slices/partnerSlice');
      
      // Use FileReader to convert file to base64 if needed, or just dummy URL
      const dummyUrl = `https://cinebook-storage.com/${type}/${file.name}`;
      
      await dispatch(uploadKYCDocument({
        userId: targetId,
        document: {
          type,
          fileName: file.name,
          fileUrl: dummyUrl,
          fileSize: file.size,
          mimeType: file.type
        }
      })).unwrap();
    } catch (err) {
      setError(`Failed to upload ${type.replace(/_/g, ' ')} artifact.`);
    } finally {
      setSaving(false);
    }
  };

  const handleManualSync = async () => {
    setSaving(true);
    if (typeof fetchUser === 'function') {
      await fetchUser();
    }
    setSaving(false);
  };

  // Auto-resume logic
  React.useEffect(() => {
    const targetId = user?.clerkId || user?.id || clerkUser?.id;
    if (targetId) {
      dispatch(checkPartnerStatus(targetId));
    }
  }, [user, clerkUser, dispatch]);

  React.useEffect(() => {
    if (onboardingStep) {
      setActiveStep(Math.min(onboardingStep - 1, 4));
    }
  }, [onboardingStep]);

  // Note: We removed the automatic redirect to dashboard here
  // to prevent race conditions during role synchronization.
  // The user will now see a finalized state or can proceed via button.

  // Pre-fill persistence logic
  React.useEffect(() => {
    if (partner) {
      if (partner.businessDetails) {
        setBusinessForm(prev => ({
          ...prev,
          ...partner.businessDetails,
          address: { ...prev.address, ...(partner.businessDetails.address || {}) }
        }));
      }
      if (partner.bankDetails) {
        setBankForm(prev => ({
          ...prev,
          ...partner.bankDetails
        }));
      }
      if (partner.currentPlan) {
        setSelectedPlan(partner.currentPlan || 'Growth');
      }
    }
  }, [partner]);

  // Form States
  const [businessForm, setBusinessForm] = useState({
    theaterDisplayName: '',
    legalEntityName: '',
    businessType: 'Pvt. Ltd.',
    gstNumber: '',
    panNumber: '',
    totalScreens: 1,
    address: { line1: '', city: '', state: '', pincode: '' }
  });

  const [selectedPlan, setSelectedPlan] = useState('Growth');
  
  const [bankForm, setBankForm] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    accountType: 'Current'
  });

  // Step Persistence Handlers
  const handleRegisterIdentity = async () => {
    const activeUser = user || clerkUser;
    
    // Industrial grade ID extraction
    const targetId = activeUser?.clerkId || 
                     activeUser?.id || 
                     clerkUser?.id;

    const targetEmail = activeUser?.email || 
                       activeUser?.userEmail || 
                       activeUser?.primaryEmailAddress?.emailAddress || 
                       clerkUser?.primaryEmailAddress?.emailAddress;

    const targetName = activeUser?.userName || 
                      activeUser?.name || 
                      activeUser?.fullName || 
                      `${activeUser?.firstName || ''} ${activeUser?.lastName || ''}`.trim() ||
                      clerkUser?.fullName ||
                      'Unassigned Identity';

    const targetPhone = activeUser?.phone || 
                       activeUser?.primaryPhoneNumber?.phoneNumber || 
                       '';
    
    if (!targetId || !targetEmail) {
      setError("Authorization Identity incomplete. High-level sync required.");
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        userId: targetId,
        userEmail: targetEmail,
        userName: targetName,
        phone: targetPhone
      };
      await dispatch(registerPartner(payload)).unwrap();
      setActiveStep(1);
    } catch (err) {
      setError(err?.message || "Identity synchronization node failed to link.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBusiness = async () => {
    const targetId = user?.clerkId || user?.id || clerkUser?.id;
    setSaving(true);
    setError('');
    try {
      await dispatch(updateBusinessDetails({
        userId: targetId,
        businessDetails: businessForm
      })).unwrap();
      setActiveStep(2);
    } catch (err) {
      setError("Business configuration handshake failed");
    } finally {
      setSaving(false);
    }
  };

  const handleKYCComplete = async () => {
    const targetId = user?.clerkId || user?.id || clerkUser?.id;
    setSaving(true);
    try {
      await dispatch(submitKYC(targetId)).unwrap();
      setActiveStep(3);
    } catch (err) {
      setError("Compliance submission node failure");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBank = async () => {
    const targetId = user?.clerkId || user?.id || clerkUser?.id;
    setSaving(true);
    setError('');
    try {
      await dispatch(updateBankDetails({
        userId: targetId,
        bankDetails: bankForm
      })).unwrap();
      setActiveStep(4);
    } catch (err) {
      setError("Bank settlement handshake failed");
    } finally {
      setSaving(false);
    }
  };

  const handleFinalSubmit = async () => {
    const targetId = user?.clerkId || user?.id || clerkUser?.id;
    setSaving(true);
    setError('');
    try {
      await dispatch(selectPlan({
        userId: targetId,
        plan: selectedPlan
      })).unwrap();
      setHandshakeMode(true);
      
      // Step 2: Force refresh user session to sync the new 'partner' role
      if (typeof fetchUser === 'function') {
        await fetchUser();
      }

      // Step 3: Industry-standard delay for cloud propagation
      setTimeout(() => {
        navigate('/partner/dashboard', { replace: true });
      }, 3000);
    } catch (err) {
      setError(err?.message || 'Plan activation failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const renderIdentityStep = () => (
    <Box sx={{ mt: 2 }} className="fade-in-up">
      <div className="form-section-header-wrap">
        <IdentityIcon className="form-section-icon" />
        <Typography className="form-section-header" style={{ marginBottom: 0 }}>Verified Identity Authority</Typography>
      </div>

      <div className="identity-verified-card">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
          <Avatar 
            sx={{ width: 80, height: 80, bgcolor: '#f1f5f9', color: '#0f172a', fontSize: '1.5rem', fontWeight: 700, border: '2px solid white', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
            src={user?.imageUrl || clerkUser?.imageUrl}
          >
            {(user?.firstName || clerkUser?.firstName)?.[0]}{(user?.lastName || clerkUser?.lastName)?.[0]}
          </Avatar>
          <Box>
            <div className="status-badge-verified">
              <ValidatedIcon sx={{ fontSize: 14 }} /> Verified Profile
            </div>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {user?.firstName || clerkUser?.firstName} {user?.lastName || clerkUser?.lastName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
              {user?.email || clerkUser?.primaryEmailAddress?.emailAddress}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 4, opacity: 0.1 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth variant="standard" label="Network Identifier" value={user?._id || user?.id || ''} disabled InputLabelProps={{ shrink: true }} className="onboarding-mui-input" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth variant="standard" label="Phone Node" value={user?.phone || 'Not Connected'} disabled InputLabelProps={{ shrink: true }} className="onboarding-mui-input" />
          </Grid>
        </Grid>
      </div>

      <Alert severity="info" sx={{ mt: 4, borderRadius: 3, bgcolor: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Provisioning a partner node requires a verified identity. Your account satisfies the <b>Security Protocol 2.1</b> requirements.
        </Typography>
      </Alert>

      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="contained" className="onboarding-btn-primary" 
          onClick={handleRegisterIdentity} disabled={saving}
          endIcon={<ValidatedIcon />}
        >
          {saving ? 'Authorizing...' : 'Authorize Partner Identity'}
        </Button>
      </Box>
    </Box>
  );

  const renderComplianceStep = () => (
    <Box sx={{ mt: 2 }} className="fade-in-up">
      <div className="form-section-header-wrap">
        <ShieldIcon className="form-section-icon" />
        <Typography className="form-section-header" style={{ marginBottom: 0 }}>Operational Compliance</Typography>
      </div>
      
      <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}>
        Please anchor your professional artifacts to the distributed ledger for verification.
      </Typography>
      
      <Stack spacing={2.5}>
        {[
          { id: 'GST_CERTIFICATE', label: 'GST Entity Registration', desc: 'Sovereign tax identifier document (PDF)', icon: <DocsIcon /> },
          { id: 'CINEMA_LICENSE', label: 'Theater Authority License', desc: 'Operational clearance node artifact', icon: <DocsIcon /> },
          { id: 'PAN_CARD', label: 'Business PAN Node', desc: 'Corporate tax identity artifact', icon: <DocsIcon /> }
        ].map((doc, i) => (
          <div className={`compliance-slot ${uploadedDocs[doc.id] ? 'uploaded' : ''}`} key={i}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <div className="slot-icon-box">
                {uploadedDocs[doc.id] ? <SuccessIcon sx={{ color: '#10b981' }} /> : doc.icon}
              </div>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>{doc.label}</Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {uploadedDocs[doc.id] ? `Artifact Anchored: ${uploadedDocs[doc.id].fileName}` : doc.desc}
                </Typography>
              </Box>
            </Box>
            <input
              type="file"
              id={`file-${doc.id}`}
              style={{ display: 'none' }}
              onChange={(e) => handleFileUpload(e, doc.id)}
            />
            <Button 
              size="small" 
              variant={uploadedDocs[doc.id] ? "text" : "outlined"} 
              startIcon={uploadedDocs[doc.id] ? <SuccessIcon /> : <UploadIcon />} 
              sx={{ 
                borderRadius: '6px', 
                textTransform: 'none', 
                fontWeight: 600, 
                border: uploadedDocs[doc.id] ? 'none' : '1px solid #e2e8f0', 
                color: uploadedDocs[doc.id] ? '#10b981' : '#64748b' 
              }}
              onClick={() => document.getElementById(`file-${doc.id}`).click()}
            >
              {uploadedDocs[doc.id] ? 'Update' : 'Upload Artifact'}
            </Button>
          </div>
        ))}
      </Stack>

      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button variant="outlined" className="onboarding-btn-secondary" onClick={handleBack}>
          Previous Step
        </Button>
        <Button 
          variant="contained" className="onboarding-btn-primary" 
          onClick={handleKYCComplete}
          disabled={saving}
        >
          {saving ? 'Synchronizing Artifacts...' : 'Confirm Artifacts'}
        </Button>
      </Box>
    </Box>
  );

  const renderBusinessStep = () => (
    <Box sx={{ mt: 2 }} className="fade-in-up">
      <div className="form-section-header-wrap">
        <BusinessIcon className="form-section-icon" />
        <Typography className="form-section-header" style={{ marginBottom: 0 }}>Business Node Parameters</Typography>
      </div>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <TextField 
            fullWidth variant="standard" label="Theater Operational Title" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={businessForm.theaterDisplayName}
            onChange={(e) => setBusinessForm({...businessForm, theaterDisplayName: e.target.value})}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField 
            fullWidth variant="standard" label="Legal Entity Registry" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={businessForm.legalEntityName}
            onChange={(e) => setBusinessForm({...businessForm, legalEntityName: e.target.value})}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="standard" className="onboarding-mui-input">
            <InputLabel shrink>Entity Structure</InputLabel>
            <Select 
              value={businessForm.businessType}
              onChange={(e) => setBusinessForm({...businessForm, businessType: e.target.value})}
            >
              <MenuItem value="Pvt. Ltd.">Private Limited Node</MenuItem>
              <MenuItem value="Proprietorship">Sole Authority</MenuItem>
              <MenuItem value="LLP">LLP / Partnership Node</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField 
            fullWidth variant="standard" label="GSTIN Registry Number" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={businessForm.gstNumber}
            onChange={(e) => setBusinessForm({...businessForm, gstNumber: e.target.value})}
          />
        </Grid>
      </Grid>

      <Typography className="form-section-header" sx={{ mt: 6, opacity: 0.6 }}>Geography Coordinates</Typography>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField 
            fullWidth variant="standard" label="Primary Distribution Address" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={businessForm.address.line1}
            onChange={(e) => setBusinessForm({...businessForm, address: {...businessForm.address, line1: e.target.value}})}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={MajorCities}
            value={businessForm.address.city}
            onChange={(e, val) => setBusinessForm({...businessForm, address: {...businessForm.address, city: val}})}
            renderInput={(params) => <TextField {...params} variant="standard" label="Hub City" InputLabelProps={{ shrink: true }} className="onboarding-mui-input" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={IndianStates}
            value={businessForm.address.state}
            onChange={(e, val) => setBusinessForm({...businessForm, address: {...businessForm.address, state: val}})}
            renderInput={(params) => <TextField {...params} variant="standard" label="Admin State" InputLabelProps={{ shrink: true }} className="onboarding-mui-input" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField 
            fullWidth variant="standard" label="Grid Pincode" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={businessForm.address.pincode}
            onChange={(e) => setBusinessForm({...businessForm, address: {...businessForm.address, pincode: e.target.value}})}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" className="onboarding-btn-secondary" onClick={handleBack}>
          Previous Step
        </Button>
        <Button variant="contained" className="onboarding-btn-primary" onClick={handleSaveBusiness} disabled={saving}>
          {saving ? 'Syncing...' : 'Synchronize Business Node'}
        </Button>
      </Box>
    </Box>
  );

  const renderSettlementStep = () => (
    <Box sx={{ mt: 2 }} className="fade-in-up">
      <div className="form-section-header-wrap">
        <SettlementIcon className="form-section-icon" />
        <Typography className="form-section-header" style={{ marginBottom: 0 }}>Financial Settlement Gateway</Typography>
      </div>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField 
            fullWidth variant="standard" label="Beneficiary Account Title" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={bankForm.accountHolderName}
            onChange={(e) => setBankForm({...bankForm, accountHolderName: e.target.value})}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField 
            fullWidth variant="standard" label="Institutional Bank Authority" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={bankForm.bankName}
            onChange={(e) => setBankForm({...bankForm, bankName: e.target.value})}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="standard" className="onboarding-mui-input">
            <InputLabel shrink>Settlement Channel Type</InputLabel>
            <Select 
              value={bankForm.accountType}
              onChange={(e) => setBankForm({...bankForm, accountType: e.target.value})}
            >
              <MenuItem value="Current">Current Ledger</MenuItem>
              <MenuItem value="Savings">Escrow / Savings</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField 
            fullWidth variant="standard" label="Conduit Account Number" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={bankForm.accountNumber}
            onChange={(e) => setBankForm({...bankForm, accountNumber: e.target.value})}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField 
            fullWidth variant="standard" label="IFSC Routing Code" 
            InputLabelProps={{ shrink: true }} className="onboarding-mui-input"
            value={bankForm.ifscCode}
            onChange={(e) => setBankForm({...bankForm, ifscCode: e.target.value})}
          />
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>}

      <Box sx={{ mt: 8, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" className="onboarding-btn-secondary" onClick={handleBack}>
          Previous
        </Button>
        <Button 
          variant="contained" className="onboarding-btn-primary" 
          onClick={handleSaveBank} disabled={saving}
        >
          {saving ? 'Syncing...' : 'Sync Settlement Node'}
        </Button>
      </Box>
    </Box>
  );

  const renderPlanStep = () => (
    <Box sx={{ mt: 2 }} className="fade-in-up">
      <div className="form-section-header-wrap">
        <PlanIcon className="form-section-icon" />
        <Typography className="form-section-header" style={{ marginBottom: 0 }}>Subscription Tier Matrix</Typography>
      </div>

      <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}>
        Select your node allocation tier to finalize the integration with the Cinema Network.
      </Typography>

      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <div 
              className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''}`}
              onClick={() => setSelectedPlan(plan.id)}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {plan.popular && <span className="plan-badge">Recommended Node</span>}
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#0f172a' }}>{plan.name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block', fontWeight: 600, minHeight: 32 }}>{plan.desc}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>{plan.price}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1, fontWeight: 700 }}>{plan.interval}</Typography>
              </Box>
              <Divider sx={{ mb: 2, opacity: 0.1 }} />
              <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
                {plan.features.map((f, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <SuccessIcon sx={{ fontSize: 16, color: plan.theme }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155' }}>{f}</Typography>
                  </Box>
                ))}
              </Stack>
              <Button 
                fullWidth 
                variant={selectedPlan === plan.id ? "contained" : "outlined"}
                sx={{ 
                  mt: 3, borderRadius: '12px', textTransform: 'none', fontWeight: 800,
                  bgcolor: selectedPlan === plan.id ? plan.theme : 'transparent',
                  color: selectedPlan === plan.id ? 'white' : '#64748b',
                  borderColor: selectedPlan === plan.id ? plan.theme : '#e2e8f0',
                  boxShadow: selectedPlan === plan.id ? `0 8px 16px ${alpha(plan.theme, 0.3)}` : 'none',
                  '&:hover': { bgcolor: selectedPlan === plan.id ? alpha(plan.theme, 0.9) : '#f8fafc' }
                }}
              >
                {selectedPlan === plan.id ? 'Deploy Strategy' : 'Select Tier'}
              </Button>
            </div>
          </Grid>
        ))}
      </Grid>

      {error && <Alert severity="error" sx={{ mt: 4, borderRadius: 2 }}>{error}</Alert>}

      <Box sx={{ mt: 8, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" className="onboarding-btn-secondary" onClick={handleBack}>
          Previous Step
        </Button>
        <Button 
          variant="contained" className="onboarding-btn-primary" 
          onClick={handleFinalSubmit} disabled={saving}
          endIcon={<SuccessIcon />}
        >
          {saving ? 'Processing Matrix...' : 'Activate Hub Membership'}
        </Button>
      </Box>
    </Box>
  );

  // Loading States: High-fidelity perceived performance
  if (!clerkLoaded || (isSignedIn && authLoading && !user)) {
    return (
      <Container maxWidth="md" sx={{ mt: 10 }}>
        <Box sx={{ width: '100%', mb: 4 }}>
          <LinearProgress sx={{ borderRadius: 5, height: 6, bgcolor: alpha('#0f172a', 0.05), '& .MuiLinearProgress-bar': { bgcolor: '#f97316' } }} />
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 4, borderRadius: 6, height: 300, display: 'flex', flexDirection: 'column', gap: 2, background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#f1f5f9', alignSelf: 'center', mb: 2 }} className="shimmer" />
              <Box sx={{ height: 24, bgcolor: '#f1f5f9', borderRadius: 2 }} className="shimmer" />
              <Box sx={{ height: 16, bgcolor: '#f1f5f9', borderRadius: 2, width: '70%' }} className="shimmer" />
              <Box sx={{ height: 40, bgcolor: '#f1f5f9', borderRadius: 3, mt: 'auto' }} className="shimmer" />
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 4, borderRadius: 6, minHeight: 400, background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(10px)' }}>
              <Stack spacing={4}>
                <Box sx={{ height: 32, bgcolor: '#f1f5f9', borderRadius: 2, width: '40%' }} className="shimmer" />
                <Box sx={{ height: 60, bgcolor: '#f1f5f9', borderRadius: 4 }} className="shimmer" />
                <Box sx={{ height: 60, bgcolor: '#f1f5f9', borderRadius: 4 }} className="shimmer" />
                <Box sx={{ height: 60, bgcolor: '#f1f5f9', borderRadius: 4 }} className="shimmer" />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
        <Typography 
          variant="caption" 
          sx={{ display: 'block', mt: 4, textAlign: 'center', fontWeight: 800, color: '#64748b', letterSpacing: '0.1em' }}
        >
          Establishing Secure Handshake with Cinema Cloud Authority...
        </Typography>
      </Container>
    );
  }

  // Final Guard: Use Clerk's isSignedIn as the primary authority
  if (!isSignedIn) {
    return (
      <Container maxWidth="sm" sx={{ mt: 10 }}>
        <Box sx={{ 
          textAlign: 'center', 
          p: 4, 
          borderRadius: 4, 
          background: 'rgba(255, 255, 255, 0.02)', 
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
        }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#fff' }}>
            Authorization Required
          </Typography>
          <Typography sx={{ mb: 4, color: 'rgba(255,255,255,0.6)' }}>
            Please sign in to your Clerk account to begin the theater partner onboarding process.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.href = '/sign-in'}
            sx={{ background: '#5856d6', '&:hover': { background: '#4a48b8' }, px: 6, py: 1.5, borderRadius: 2 }}
          >
            Go to Sign In
          </Button>
        </Box>
      </Container>
    );
  }

  if (handshakeMode) {
    return (
      <Box sx={{ 
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 50%, #1a1b20 0%, #0a0b0d 100%)',
        flexDirection: 'column', p: 4, textAlign: 'center'
      }}>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <Box sx={{ position: 'relative', width: 200, height: 200, mb: 4 }}>
            <CircularProgress 
              variant="determinate" value={handshakeProgress} 
              size={200} thickness={1.5} 
              sx={{ color: '#6366f1', position: 'absolute', top: 0, left: 0 }}
            />
            <CircularProgress 
              size={200} thickness={1} 
              sx={{ color: alpha('#6366f1', 0.1), position: 'absolute', top: 0, left: 0 }}
            />
            <Box sx={{ 
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column'
            }}>
              <SyncIcon sx={{ fontSize: 60, color: '#6366f1', mb: 1, animation: 'spin 4s linear infinite' }} />
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 950, color: '#fff', letterSpacing: '0.1em' }}>
                {handshakeProgress}%
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ fontSize: '1.8rem', fontWeight: 950, color: '#fff', mb: 1, letterSpacing: '-0.02em' }}>
            Finalizing Hub Integration
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: alpha('#fff', 0.5), fontWeight: 500, maxWidth: 400, mx: 'auto' }}>
            {handshakeProgress < 40 ? "Verifying Node Identity..." : 
             handshakeProgress < 70 ? "Synchronizing Neural Permissions..." : 
             "Finalizing Network Handshake..."}
          </Typography>
        </motion.div>
        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      </Box>
    );
  }

  return (
    <div className="onboarding-page-wrapper">
      <Container maxWidth="md">
        <header className="onboarding-header">
          <Typography className="onboarding-title">Partner Onboarding</Typography>
          <Typography className="onboarding-subtitle">Establish your presence in the Cinema Network</Typography>
        </header>

        <Paper className="onboarding-main-card">
          <Box className="onboarding-card-content">
            <Stepper activeStep={activeStep} className="onboarding-stepper" alternativeLabel>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel StepIconComponent={() => (
                    <Box sx={{ 
                      width: 32, height: 32, borderRadius: '50%', 
                      bgcolor: activeStep === index ? '#f97316' : activeStep > index ? '#0f172a' : '#f1f5f9',
                      color: activeStep >= index ? 'white' : '#64748b',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.8rem', fontWeight: 800
                    }}>
                      {activeStep > index ? <SuccessIcon sx={{ fontSize: 16 }} /> : index + 1}
                    </Box>
                  )}>
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && renderIdentityStep()}
            {activeStep === 1 && renderBusinessStep()}
            {activeStep === 2 && renderComplianceStep()}
            {activeStep === 3 && renderSettlementStep()}
            {activeStep === 4 && renderPlanStep()}
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default PartnerOnboarding;
